import { BannerElement, BannerSize, DataFeed, ExportOptions } from '../types';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Types
interface Element {
  id: string;
  type: 'text' | 'image' | 'shape' | 'button' | 'divider';
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  content?: string;
  imageUrl?: string;
  dataBinding?: string;
  styles: {
    fontSize?: number;
    fontWeight?: string;
    fontFamily?: string;
    textAlign?: string;
    color?: string;
    backgroundColor?: string;
    borderRadius?: number;
    padding?: number;
    margin?: number;
    boxShadow?: string;
    border?: string;
    opacity?: number;
    transform?: string;
  };
}

interface Size {
  width: number;
  height: number;
  name: string;
}

interface PlatformOptimizations {
  platform?: 'google-ads' | 'facebook-meta';
  clickTracking?: boolean;
}

interface ExportBannerSetOptions {
  bannerSet: {
    dataFeedArray: DataFeed[];
  };
  selectedSizes: Size[];
  format: 'html' | 'png' | 'jpg';
  quality?: number;
  platforms?: string[];
}

// Consolidated export utilities with platform support
export const exportToHTML = (
  elements: BannerElement[],
  size: BannerSize,
  dataFeed: DataFeed | null,
  includeAnimations = true,
  platformOptimizations?: {
    platform?: string;
    clickTracking?: boolean;
  }
) => {
  const processContent = (content: string, dataBinding?: string) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || content);
    }
    
    if (!dataFeed) return content;
    
    return content.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  const processImageUrl = (imageUrl: string, dataBinding?: string) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || imageUrl);
    }
    
    if (!dataFeed) return imageUrl;
    
    return imageUrl.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  const generateElementHTML = (element: BannerElement): string => {
    const baseStyles = `
      position: absolute;
      left: ${element.x}px;
      top: ${element.y}px;
      width: ${element.width}px;
      height: ${element.height}px;
      z-index: ${element.zIndex};
      ${element.styles.fontSize ? `font-size: ${element.styles.fontSize}px;` : ''}
      ${element.styles.fontWeight ? `font-weight: ${element.styles.fontWeight};` : ''}
      ${element.styles.fontFamily ? `font-family: ${element.styles.fontFamily};` : ''}
      ${element.styles.textAlign ? `text-align: ${element.styles.textAlign};` : ''}
      ${element.styles.color ? `color: ${element.styles.color};` : ''}
      ${element.styles.backgroundColor ? `background-color: ${element.styles.backgroundColor};` : ''}
      ${element.styles.borderRadius ? `border-radius: ${element.styles.borderRadius}px;` : ''}
      ${element.styles.padding ? `padding: ${element.styles.padding}px;` : ''}
      ${element.styles.margin ? `margin: ${element.styles.margin}px;` : ''}
      ${element.styles.boxShadow ? `box-shadow: ${element.styles.boxShadow};` : ''}
      ${element.styles.border ? `border: ${element.styles.border};` : ''}
      ${element.styles.opacity !== undefined ? `opacity: ${element.styles.opacity};` : ''}
      ${element.styles.transform ? `transform: ${element.styles.transform};` : ''}
    `.trim();

    const className = `element-${element.id}`;

    switch (element.type) {
      case 'text':
        return `<div class="${className}" style="${baseStyles} display: flex; align-items: center; justify-content: center;">${processContent(element.content || 'Text', element.dataBinding)}</div>`;

      case 'image':
        return `<div class="${className}" style="${baseStyles} overflow: hidden;"><img src="${processImageUrl(element.imageUrl || '', element.dataBinding)}" alt="Banner Image" style="width: 100%; height: 100%; object-fit: cover;"></div>`;

      case 'shape':
        return `<div class="${className}" style="${baseStyles}"></div>`;

      case 'button':
        const clickHandler = platformOptimizations?.clickTracking ? 
          'onclick="window.open(clickTag, \'_blank\')"' : '';
        return `<button class="${className}" style="${baseStyles} display: flex; align-items: center; justify-content: center; border: none; cursor: pointer;" ${clickHandler}>${processContent(element.content || 'Button', element.dataBinding)}</button>`;

      case 'divider':
        return `<div class="${className}" style="${baseStyles}"></div>`;

      default:
        return '';
    }
  };

  const sortedElements = elements.sort((a, b) => a.zIndex - b.zIndex);
  const elementsHTML = sortedElements.map(generateElementHTML).join('\n    ');
  
  // Collect custom fonts used in the banner
  const customFonts = new Set<string>();
  elements.forEach(element => {
    if (element.styles.fontFamily && element.styles.fontFamily.startsWith('custom-')) {
      customFonts.add(element.styles.fontFamily);
    }
  });

  // Generate @font-face declarations for custom fonts
  let fontFaceCSS = '';
  if (customFonts.size > 0) {
    // Get custom fonts from localStorage
    const storedFonts = localStorage.getItem('dealercentives_custom_fonts');
    if (storedFonts) {
      try {
        const fonts = JSON.parse(storedFonts);
        fonts.forEach((font: any) => {
          if (customFonts.has(font.family)) {
            fontFaceCSS += `
        @font-face {
            font-family: '${font.family}';
            src: url('${font.url}') format('${font.format}');
            font-display: swap;
        }`;
          }
        });
      } catch (error) {
        console.warn('Failed to load custom fonts for export:', error);
      }
    }
  }
  
  // Platform-specific head content
  let platformHead = '';
  if (platformOptimizations?.platform) {
    switch (platformOptimizations.platform) {
      case 'google-ads':
        platformHead = `
    <meta name="ad.size" content="width=${size.width},height=${size.height}">
    <script>var clickTag = "%%CLICK_URL_UNESC%%%%DEST_URL%%";</script>`;
        break;
      case 'facebook-meta':
        platformHead = `
    <meta property="og:title" content="Advertisement">
    <meta property="og:description" content="${dataFeed?.model || 'Special Offer'}">
    <meta property="og:image" content="${dataFeed?.image_url || ''}">`;
        break;
    }
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>DealerCentives Banner</title>${platformHead}
    <style>${fontFaceCSS}
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        .banner-container {
            position: relative;
            width: ${size.width}px;
            height: ${size.height}px;
            background-color: #ffffff;
            overflow: hidden;
        }
        
        @media (max-width: 768px) {
            .banner-container {
                max-width: 100%;
                height: auto;
                aspect-ratio: ${size.width}/${size.height};
            }
            
            .banner-container > * {
                transform-origin: top left;
                transform: scale(calc(100vw / ${size.width}));
            }
        }
    </style>
</head>
<body>
    <div class="banner-container">
        ${elementsHTML}
    </div>
</body>
</html>`;
};

export const exportToCanvas = async (
  elements: BannerElement[],
  size: BannerSize,
  dataFeed: DataFeed | null
): Promise<HTMLCanvasElement> => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = `${size.width}px`;
  container.style.height = `${size.height}px`;
  container.style.backgroundColor = '#ffffff';
  document.body.appendChild(container);

  const sortedElements = elements.sort((a, b) => a.zIndex - b.zIndex);
  
  for (const element of sortedElements) {
    const elementDiv = document.createElement('div');
    elementDiv.style.position = 'absolute';
    elementDiv.style.left = `${element.x}px`;
    elementDiv.style.top = `${element.y}px`;
    elementDiv.style.width = `${element.width}px`;
    elementDiv.style.height = `${element.height}px`;
    
    Object.entries(element.styles).forEach(([key, value]) => {
      if (value !== undefined) {
        (elementDiv.style as any)[key] = value;
      }
    });

    if (element.type === 'text' || element.type === 'button') {
      elementDiv.textContent = element.dataBinding && dataFeed 
        ? String(dataFeed[element.dataBinding] || element.content || '')
        : element.content || '';
      elementDiv.style.display = 'flex';
      elementDiv.style.alignItems = 'center';
      elementDiv.style.justifyContent = 'center';
    } else if (element.type === 'image') {
      const img = document.createElement('img');
      img.src = element.dataBinding && dataFeed 
        ? String(dataFeed[element.dataBinding] || element.imageUrl || '')
        : element.imageUrl || '';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      elementDiv.appendChild(img);
    }

    container.appendChild(elementDiv);
  }

  const images = container.querySelectorAll('img');
  await Promise.all(Array.from(images).map(img => {
    return new Promise((resolve) => {
      if (img.complete) {
        resolve(null);
      } else {
        img.onload = () => resolve(null);
        img.onerror = () => resolve(null);
      }
    });
  }));

  const canvas = await html2canvas(container, {
    width: size.width,
    height: size.height,
    backgroundColor: '#ffffff',
    useCORS: true
  });

  document.body.removeChild(container);
  return canvas;
};

// Consolidated export functions
export const exportBannerSet = async ({
  bannerSet,
  selectedSizes,
  format,
  quality,
  platforms
}: {
  bannerSet: any;
  selectedSizes: BannerSize[];
  format: 'html' | 'png' | 'jpeg';
  quality?: number;
  platforms?: string[];
}) => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const zip = new JSZip();
  
  const sanitizeFilename = (filename: string): string => {
    return filename.toLowerCase().replace(/[^a-z0-9\-_]/g, '-').replace(/-+/g, '-');
  };

  if (platforms && platforms.length > 0) {
    // Platform-optimized export
    for (const platform of platforms) {
      const platformFolder = zip.folder(sanitizeFilename(platform));
      if (!platformFolder) continue;

      for (const size of selectedSizes) {
        const sizeFolder = platformFolder.folder(sanitizeFilename(size.name));
        if (!sizeFolder) continue;

        for (let i = 0; i < bannerSet.dataFeedArray.length; i++) {
          const dataFeed = bannerSet.dataFeedArray[i];
          const filename = `${sanitizeFilename(platform)}_${sanitizeFilename(size.name)}_${String(i + 1).padStart(3, '0')}_${sanitizeFilename(dataFeed.model || 'item')}.${format}`;
          
          try {
            if (format === 'html') {
              const html = exportToHTML(size.elements, size, dataFeed, true, {
                platform,
                clickTracking: true
              });
              sizeFolder.file(filename, html);
            } else {
              const canvas = await exportToCanvas(size.elements, size, dataFeed);
              const blob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((blob) => resolve(blob!), 
                  format === 'png' ? 'image/png' : 'image/jpeg',
                  quality ? quality / 100 : 0.9
                );
              });
              sizeFolder.file(filename, blob);
            }
          } catch (error) {
            console.error(`Error generating file ${filename}:`, error);
          }
        }
      }
    }
  } else {
    // Standard export
    for (const size of selectedSizes) {
      const sizeFolder = zip.folder(sanitizeFilename(size.name));
      if (!sizeFolder) continue;
      
      for (let i = 0; i < bannerSet.dataFeedArray.length; i++) {
        const dataFeed = bannerSet.dataFeedArray[i];
        const filename = `${sanitizeFilename(size.name)}_${String(i + 1).padStart(3, '0')}_${sanitizeFilename(dataFeed.model || 'item')}.${format}`;
        
        try {
          if (format === 'html') {
            const html = exportToHTML(size.elements, size, dataFeed, true);
            sizeFolder.file(filename, html);
          } else {
            const canvas = await exportToCanvas(size.elements, size, dataFeed);
            const blob = await new Promise<Blob>((resolve) => {
              canvas.toBlob((blob) => resolve(blob!), 
                format === 'png' ? 'image/png' : 'image/jpeg',
                quality ? quality / 100 : 0.9
              );
            });
            sizeFolder.file(filename, blob);
          }
        } catch (error) {
          console.error(`Error generating file ${filename}:`, error);
        }
      }
    }
  }
  
  const readme = `DealerCentives Banner Export
Generated: ${new Date().toLocaleString()}
Banner Set: ${bannerSet.name}
Format: ${format.toUpperCase()}
${quality ? `Quality: ${quality}%` : ''}

Exported Sizes:
${selectedSizes.map(size => `- ${size.name} (${size.width}×${size.height})`).join('\n')}

Data Variations: ${bannerSet.dataFeedArray.length}
Total Files: ${selectedSizes.length * bannerSet.dataFeedArray.length}

Custom Fonts:
${getCustomFontsUsed(selectedSizes).length > 0 ? 
  getCustomFontsUsed(selectedSizes).map(font => `- ${font}`).join('\n') : 
  'No custom fonts used'}
`;
  
  zip.file('README.txt', readme);
  
  const zipBlob = await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
  
  const zipFilename = `${sanitizeFilename(bannerSet.name)}_${format}_${timestamp}.zip`;
  saveAs(zipBlob, zipFilename);
};

// Helper function to get custom fonts used in banner sizes
const getCustomFontsUsed = (sizes: BannerSize[]): string[] => {
  const customFonts = new Set<string>();
  
  sizes.forEach(size => {
    size.elements.forEach(element => {
      if (element.styles.fontFamily && element.styles.fontFamily.startsWith('custom-')) {
        customFonts.add(element.styles.fontFamily);
      }
    });
  });
  
  return Array.from(customFonts);
};

export const exportBanner = async (
  elements: BannerElement[],
  size: BannerSize,
  dataFeed: DataFeed | null,
  dataFeedArray: DataFeed[],
  options: ExportOptions
) => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');

  if (options.batchExport && dataFeedArray.length > 1) {
    const zip = new JSZip();
    
    for (let i = 0; i < dataFeedArray.length; i++) {
      const currentData = dataFeedArray[i];
      const filename = `banner-${i + 1}-${currentData.model || 'item'}-${timestamp}`;
      
      if (options.format === 'html') {
        const html = exportToHTML(elements, size, currentData, options.includeAnimations);
        zip.file(`${filename}.html`, html);
      } else {
        const canvas = await exportToCanvas(elements, size, currentData);
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), 
            options.format === 'png' ? 'image/png' : 'image/jpeg',
            options.quality ? options.quality / 100 : 0.9
          );
        });
        zip.file(`${filename}.${options.format}`, blob);
      }
    }
    
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `banners-batch-${timestamp}.zip`);
  } else {
    const filename = `banner-${size.name}-${timestamp}`;
    
    if (options.format === 'html') {
      const html = exportToHTML(elements, size, dataFeed, options.includeAnimations);
      downloadFile(html, `${filename}.html`, 'text/html');
    } else {
      const canvas = await exportToCanvas(elements, size, dataFeed);
      canvas.toBlob((blob) => {
        if (blob) {
          saveAs(blob, `${filename}.${options.format}`);
        }
      }, 
      options.format === 'png' ? 'image/png' : 'image/jpeg',
      options.quality ? options.quality / 100 : 0.9
      );
    }
  }
};

export const exportToJSON = (
  elements: BannerElement[],
  size: BannerSize,
  templateName: string
) => {
  return JSON.stringify({
    name: templateName,
    size,
    elements,
    exportedAt: new Date().toISOString()
  }, null, 2);
};

export const downloadFile = (content: string, filename: string, type: string) => {
  const blob = new Blob([content], { type });
  saveAs(blob, filename);
};